import { useApolloClient } from '@apollo/client';
import {
  Avatar,
  Button,
  ButtonList,
  Col,
  Container,
  Dialog,
  DialogHeader,
  Divider,
  Justify,
  Position,
  Row,
  Size,
  Tabs,
  Tray,
} from '@busybusy/webapp-react-ui';
import { Equipment, EquipmentModelType, OrganizationCustomFieldsTypes } from '__generated__/graphql';
import { EQUIPMENT_BY_ID_QUERY } from 'apollo/queries/equipment-queries';
import { ArrowBackIcon } from 'assets/icons';
import cameraImage from 'assets/images/camera.png';
import classNames from 'classnames';
import { DetailsCard, DetailsItem, IconButton, MoreButton } from 'components';
import EditEquipmentFormDialog from 'components/domain/equipment/EditEquipmentDialog/EditEquipmentDialog';
import useIntegratedEquipmentEnabled from 'components/domain/equipment/IntegratedEquipmentLocationDialog/hooks/useIntegratedEquipmentEnabled';
import DetailsItemWrapper from 'components/foundation/details-item/DetailsItemWrapper/DetailsItemWrapper';
import useEquipmentChecklists from 'containers/equipment-inspections/hooks/useEquipmentChecklists';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useCurrencyFormatter, useOpenable } from 'hooks';
import useEquipmentInspectionsEnabled from 'hooks/feature/equipment/useEquipmentInspectionsEnabled/useEquipmentInspectionsEnabled';
import useDeleteEquipmentCostHistory from 'hooks/models/equipment-cost-history/useDeleteEquipmentCostHistory';
import useEquipmentDevices, { IEquipmentDevice } from 'hooks/models/equipment/useEquipmentDevices';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { first, isNil, startCase } from 'lodash';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { ClassName } from 'types/ClassName';
import IEquipment from 'types/Equipment';
import { equipmentUtils } from 'utils';
import { equipmentImageUrl, getCurrentEquipmentCostHistory, getFutureEquipmentCostHistory } from 'utils/equipmentUtils';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import {
  ICustomFieldSimpleObject,
  ICustomFieldTableRow,
  extractCustomFields,
} from 'utils/organizationCustomFieldsUtils';
import EquipmentCertificationsDetails from './EquipmentCertificationsDetails/EquipmentCertificationsDetails';
import EquipmentCostHistoryDialog from './EquipmentCostHistoryDialog/EquipmentCostHistoryDialog';
import ScheduleEquipmentHourlyCostChange from './ScheduleEquipmentHourlyCostChange/ScheduleEquipmentHourlyCostChange';
export interface IManageEquipmentDetailsDialogProps {
  className?: ClassName;
  equipment: Equipment | IEquipment;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (equipment: Equipment | IEquipment) => void;
  onDelete: (equipment: Equipment | IEquipment) => void;
}

const ManageEquipmentDetailsDialog = (props: IManageEquipmentDetailsDialogProps) => {
  const { className, equipment, isOpen, onClose, onEdit, onDelete } = props;

  const [data, setData] = useState<Equipment | IEquipment>(equipment);
  const editDetails = useOpenable();
  const [editKey, setEditKey] = useState<string | null>(null);
  const deleteDetails = useOpenable();
  const equipmentCostHistoryDetails = useOpenable();
  const client = useApolloClient();
  const futureEquipmentCostHistory = getFutureEquipmentCostHistory(data.costHistory ?? []);
  const deleteEquipmentCostHistory = useDeleteEquipmentCostHistory();
  const currencyFormatter = useCurrencyFormatter();
  const { getOrganizationCustomFields } = useOrganizationCustomFields();
  const { getEquipmentChecklists } = useEquipmentChecklists();
  const [tabValue, setTabValue] = useState('details');
  const isCertificationStatusEnabled = useFeatureFlags('CERTIFICATIONS');
  const hasGpsHardware = useIntegratedEquipmentEnabled();
  const fetchDevices = useEquipmentDevices();
  const [selectedDevice, setSelectedDevice] = useState<IEquipmentDevice | null>(null);
  const equipmentInspectionsEnabled = useEquipmentInspectionsEnabled();

  const [customFields, setCustomFields] = useState<ICustomFieldTableRow[]>([]);
  const [routineInspectionIntervals, setRoutineInspectionIntervals] = useState<string>();

  useOnMount(() => {
    getCustomFields();
    if (hasGpsHardware) {
      getDevices();
    }
    getRoutineInspectionIntervals();
  });

  const getRoutineInspectionIntervals = async () => {
    const checklists = await getEquipmentChecklists();
    const routineIds = new Set(data.routines?.map((r) => r.equipmentInspectionChecklistId));

    const intervals = checklists.filter((c) => routineIds.has(c.id)).map((c) => c.checklistTitle);
    const intervalString = intervals.join('\n');
    setRoutineInspectionIntervals(intervalString);
  };

  const getCustomFields = async () => {
    const organizationCustomFields = await getOrganizationCustomFields(OrganizationCustomFieldsTypes.Equipment);
    if (organizationCustomFields) {
      const extractedFields = extractCustomFields(organizationCustomFields);
      setCustomFields(extractedFields);
    }
  };
  const getDevices = async () => {
    const allDevices = await fetchDevices(hasGpsHardware);
    setSelectedDevice(allDevices.find((device) => device.equipment_id === equipment.id) ?? null);
  };

  function handleDelete() {
    onDelete(data);
  }

  function handleDetailItemClick(key: string) {
    setEditKey(key);
    editDetails.open();
  }

  function handleEditClose() {
    setEditKey(null);
    editDetails.close();
  }

  async function getEquipment(id: string) {
    const results = await client.query<{ equipment: Equipment[] }>({
      query: EQUIPMENT_BY_ID_QUERY,
      variables: {
        equipmentId: id,
      },
      fetchPolicy: 'network-only',
    });
    return results.data.equipment;
  }

  async function handleEditSubmit(equipmentId?: string) {
    if (!isNil(equipmentId)) {
      const newEquipment = first(await getEquipment(equipmentId));
      if (newEquipment) {
        onEdit(newEquipment);
        setData(newEquipment);
      }
    }
    if (data.id) {
      const refreshedEquipment = first(await getEquipment(data.id));
      if (refreshedEquipment) {
        setData(refreshedEquipment);
      }
    }
    setEditKey(null);
    await getCustomFields();
    await getDevices();
    editDetails.close();
  }

  function curriedDetailItemClick(key: string) {
    return () => handleDetailItemClick(key);
  }

  function handleAvatarClick(e: React.MouseEvent<HTMLElement, MouseEvent>) {
    handleDetailItemClick('image');
  }

  function getEquipmentCostHistoryValue() {
    const latestHistory = getCurrentEquipmentCostHistory(data.costHistory ?? []);
    return latestHistory ? currencyFormatter(latestHistory.operatorCostRate ?? 0) + '/' + t('hr') : '---';
  }

  const renderDropDown = (closeDropdown: () => void) => {
    const handleViewHourlyCostHistory = (event: React.MouseEvent) => {
      closeDropdown();
      event.stopPropagation();
      event.preventDefault();
      equipmentCostHistoryDetails.open();
    };

    return (
      <ButtonList>
        <Button onClick={handleViewHourlyCostHistory}>{t('View Hourly Rate History')}</Button>
      </ButtonList>
    );
  };

  async function handleEquipmentCostHistoryDelete() {
    if (!isNil(futureEquipmentCostHistory)) {
      await deleteEquipmentCostHistory(futureEquipmentCostHistory.id);
      await handleEditSubmit(data.id);
    }
  }

  function handleOnTabChange(value: string) {
    setTabValue(value);
  }

  const classes = classNames('manage-equipment-details', className);

  const renderedCustomFields = useMemo(() => {
    const customFieldObjects: ICustomFieldSimpleObject[] = customFields.map((field, index) => {
      const customFieldKey = `customField${index + 1}` as keyof typeof equipment;
      return {
        fieldName: field.title,
        value: data?.[customFieldKey] !== undefined ? String(data?.[customFieldKey]) : '---',
      };
    });

    if (customFieldObjects.length === 0) return null;

    return (
      <div className="pt-6">
        <DetailsCard title={t('Custom Fields')}>
          {customFieldObjects.map((field, index) => (
            <DetailsItem
              key={index}
              title={field.fieldName}
              onClick={curriedDetailItemClick(`customField${index + 1}`)}
              value={field.value ?? '---'}
            />
          ))}
        </DetailsCard>
      </div>
    );
  }, [customFields, data]);

  return (
    <>
      <Dialog size="full" isOpen={isOpen} hasDismiss={false} onClose={onClose}>
        <div className={classes}>
          <DialogHeader divider={true}>
            <IconButton svg={ArrowBackIcon} onClick={onClose} />
          </DialogHeader>
          <Container className="py-4 px-8">
            <div>
              <Row justify={Justify.SPACE_BETWEEN} className="py-5">
                <Col align="center">
                  <Tray spacing={1}>
                    <Avatar
                      firstName={data.equipmentName.toUpperCase()}
                      size={Size.MEDIUM}
                      onClick={handleAvatarClick}
                      image={data.imageUrl ? equipmentImageUrl(data) : cameraImage}
                    />
                    <Col className="ml-3" align="start">
                      <h1 className="pb-1">{data.equipmentName}</h1>
                      <div className="subtitle fc-2">{equipmentUtils.makeModelString(data)}</div>
                    </Col>
                  </Tray>
                </Col>
                <Col align="start" className="align-right">
                  <Tray align="right" spacing={1}>
                    <MoreButton position={Position.BOTTOM_END} size={Size.MEDIUM} renderContent={renderDropDown} />
                    <Button type="secondary" onClick={deleteDetails.open}>
                      {t('Delete')}
                    </Button>
                  </Tray>
                </Col>
              </Row>
              {isCertificationStatusEnabled && (
                <Tabs value={tabValue} onChange={handleOnTabChange}>
                  <Button value="details">{t('Details')}</Button>
                  <Button value="certifications">{t('Certifications')}</Button>
                </Tabs>
              )}
              <Divider />
              {tabValue === 'details' && (
                <Row className="pt-6 pb-4" gutter={10}>
                  <Col size={5}>
                    <DetailsCard title={t('General')}>
                      <DetailsItem
                        title={t('Name')}
                        onClick={curriedDetailItemClick('name')}
                        value={data.equipmentName}
                      />
                      <DetailsItemWrapper onClick={curriedDetailItemClick('make')}>
                        <HeaderText title={t('Type')}>{startCase(data.model?.type) ?? ''}</HeaderText>
                        <HeaderText title={t('Category')} className={'my-5'}>
                          {data.model?.category.title ?? ''}
                        </HeaderText>
                        <HeaderText title={t('Make')} className={'my-5'}>
                          {data.model?.make ? (data.model.make.unknown ? '---' : data.model.make.title) : ''}
                        </HeaderText>
                        <HeaderText title={t('Model')}>
                          {data.model ? (data.model.unknown ? '---' : data.model.title) : ''}
                        </HeaderText>
                      </DetailsItemWrapper>
                      <DetailsItem
                        title={t('Year')}
                        value={
                          'year' in data
                            ? data.year
                              ? `${data.year}`
                              : '---'
                            : data.model?.year
                              ? `${data.model.year}`
                              : '---'
                        }
                        onClick={curriedDetailItemClick('year')}
                      />
                    </DetailsCard>
                  </Col>
                  <Col size={5}>
                    <DetailsCard title={t('Other')}>
                      {data.model?.type === EquipmentModelType.Equipment && (
                        <DetailsItem
                          title={t('Hour Meter')}
                          onClick={curriedDetailItemClick('hourMeter')}
                          value={
                            data.trackManualHours
                              ? data.lastHours?.runningHours
                                ? data.lastHours?.runningHours?.toString()
                                : '---'
                              : '---'
                          }
                        />
                      )}
                      <DetailsItem
                        title={t('Hourly Cost')}
                        onClick={curriedDetailItemClick('hourlyCost')}
                        value={getEquipmentCostHistoryValue()}
                        hint={t(
                          'The cost to run the item for one hour, minus labor costs. This should be the total of all non-operator costs such as fuel, maintenance, etc.'
                        )}
                        disabled={!isNil(futureEquipmentCostHistory)}
                      />
                      {hasGpsHardware && (
                        <DetailsItem
                          title={t('Tracking Device')}
                          onClick={curriedDetailItemClick('gpsDevice')}
                          value={selectedDevice ? selectedDevice.esn : '---'}
                        />
                      )}
                    </DetailsCard>
                    {futureEquipmentCostHistory && (
                      <ScheduleEquipmentHourlyCostChange
                        costHistory={futureEquipmentCostHistory}
                        onDelete={handleEquipmentCostHistoryDelete}
                      />
                    )}
                    {renderedCustomFields}
                    {equipmentInspectionsEnabled && (
                      <DetailsCard title={t('Inspections')} className={'pt-6'}>
                        <DetailsItem
                          title={t('Pre-Operation Inspection')}
                          value={data.preOperationChecklist?.name ?? '---'}
                          onClick={curriedDetailItemClick('preOperationChecklist')}
                        />
                        <DetailsItem
                          title={t('Post-Operation Inspection')}
                          value={data.postOperationChecklist?.name ?? '---'}
                          onClick={curriedDetailItemClick('postOperationChecklist')}
                        />
                        <DetailsItem
                          title={t('Routines')}
                          value={routineInspectionIntervals ?? '---'}
                          onClick={curriedDetailItemClick('routines')}
                          preserveWhitespace={true}
                        />
                      </DetailsCard>
                    )}
                  </Col>
                </Row>
              )}
              {tabValue === 'certifications' && <EquipmentCertificationsDetails equipment={data} />}
            </div>
          </Container>
        </div>
      </Dialog>
      {editKey && (
        <EditEquipmentFormDialog
          isOpen={editDetails.isOpen}
          onClose={handleEditClose}
          equipmentId={data.id}
          editKey={editKey}
          onSubmit={handleEditSubmit}
        />
      )}
      <DeleteConfirmationDialog
        isOpen={deleteDetails.isOpen}
        onClose={deleteDetails.close}
        onDelete={handleDelete}
        title={t('Delete equipment?')}
        showHeaderDivider={false}
      >
        {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
      </DeleteConfirmationDialog>
      <EquipmentCostHistoryDialog
        equipment={data}
        isOpen={equipmentCostHistoryDetails.isOpen}
        onClose={() => {
          handleEditSubmit(data.id).then(() => {
            equipmentCostHistoryDetails.close();
          });
        }}
        onChange={() => {
          handleEditSubmit(data.id);
        }}
      />
    </>
  );
};

export default ManageEquipmentDetailsDialog;
